import React, { useState } from "react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { api } from "../lib/api";
import { Switch } from "./ui/switch";

const btn = "font-sans-ui text-[9px] uppercase tracking-[0.25em] px-2 py-1 border transition disabled:opacity-40";

const WalkOnRow = ({ storyId, npc, onChanged }) => {
  const [note, setNote] = useState(npc.note || "");
  const [busy, setBusy] = useState(false);
  const label = npc.label;
  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      if (okMsg) toast.success(typeof okMsg === "function" ? okMsg(r) : okMsg);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };
  const saveNote = () => {
    if (note.trim() === (npc.note || "").trim()) return;
    run(() => api.patch(`/stories/${storyId}/npcs/${label}`, { note }), "Walk-on note saved");
  };
  const promote = () => {
    if (!window.confirm(`Turn ${npc.name} into a full character card?`)) return;
    run(() => api.post(`/stories/${storyId}/npcs/${label}/promote`), (r) =>
      r.data.added_to_story
        ? `${npc.name} joined the cast. Edit their card in Characters.`
        : `${npc.name} saved to your library (cast is full, 4 max).`);
  };
  const forget = () => run(() => api.delete(`/stories/${storyId}/npcs/${label}`), `${npc.name} forgotten`);
  return (
    <div data-testid={`walkon-row-${label}`} className="px-1 py-1.5 space-y-1.5 border-t border-stone-800/60 first:border-t-0">
      <div className="flex items-center justify-between gap-2">
        <span className="font-serif-reading italic text-stone-300 text-xs">
          {npc.name}
          <span className="ml-2 not-italic font-sans-ui text-[9px] uppercase tracking-[0.2em] text-stone-500">
            seen {npc.appearances || 1}×
          </span>
        </span>
        <div className="flex gap-1.5">
          <button type="button" data-testid={`walkon-promote-${label}`} disabled={busy} onClick={promote}
            className={`${btn} text-rose-300 border-rose-800 hover:bg-rose-950/40`}>Promote</button>
          <button type="button" data-testid={`walkon-forget-${label}`} disabled={busy} onClick={forget}
            className={`${btn} text-stone-600 border-stone-800 hover:text-rose-300 hover:border-rose-800`}>Forget</button>
        </div>
      </div>
      <input
        data-testid={`walkon-note-${label}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={saveNote}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        placeholder={npc.first_line ? `Note (first said: "${npc.first_line.slice(0, 60)}")` : "Who are they? e.g. gruff night-shift bartender"}
        className="w-full bg-stone-900 border border-stone-800 text-stone-200 font-serif-reading text-xs px-2 py-1.5 rounded-sm focus:outline-none focus:border-rose-800 placeholder:text-stone-600"
      />
    </div>
  );
};

export const WalkOnsPanel = ({ storyId, npcs, enabled, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const toggle = async (v) => {
    setSaving(true);
    try {
      await api.patch(`/stories/${storyId}/walk-ons`, { enabled: v });
      toast.success(v ? "Walk-ons on for this story" : "Walk-ons off — only your cast speaks");
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update walk-ons");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="walkons-panel" className="border border-stone-800/60 bg-stone-900/30 rounded-2xl p-3 space-y-2">
      <button
        type="button"
        data-testid="walkons-toggle"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-1 py-1 text-stone-500 hover:text-rose-300 font-sans-ui text-[9px] uppercase tracking-[0.25em] transition"
      >
        <span className="flex items-center gap-2">
          <UserPlus className="w-3 h-3" strokeWidth={1.5} />
          {open ? "▾" : "▸"} Walk-ons ({npcs.length}){!enabled && " · off"}
        </span>
        <span className="text-stone-500 normal-case tracking-normal text-[10px]">minor characters</span>
      </button>
      {open && (
        <div className="pt-1">
          <label className="flex items-center justify-between gap-2 px-1 pb-2">
            <span className="font-serif-reading italic text-[11px] text-stone-400">
              {enabled ? "AI may add brief walk-ons (waiters, strangers…)" : "Off — only your cast speaks"}
            </span>
            <Switch data-testid="walkons-enabled-switch" checked={enabled} disabled={saving} onCheckedChange={toggle} />
          </label>
          {npcs.length === 0 ? (
            <p data-testid="walkons-empty" className="px-1 font-serif-reading italic text-[11px] text-stone-600">
              No walk-ons yet. When a waiter, cop or stranger speaks in a scene, they'll show up here so you can keep them consistent or promote them to the cast.
            </p>
          ) : (
            npcs.map(n => <WalkOnRow key={n.label} storyId={storyId} npc={n} onChanged={onChanged} />)
          )}
        </div>
      )}
    </div>
  );
};
